import React, { useState, useRef, useEffect } from 'react';
import { Utils, authJsonHeaders } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faShieldAlt, faCheck, faCopy, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import './TwoFactorSetup.css';

function TwoFactorSetup({ userId, onComplete, onCancel, onSkip = null, isMandatory = false, pending2FAToken = null }) {
  const [step, setStep] = useState(1); // 1: intro, 2: scan QR, 3: verify, 4: recovery codes
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryCopied, setRecoveryCopied] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef([]);

  // Auto-focus first input when step changes to 3
  useEffect(() => {
    if (step === 3 && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  const startSetup = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(Utils.baseurl + '2fa/setup', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.token, pending2FAToken),
        body: JSON.stringify({ userId }),
      });

      const result = await response.json();

      if (result.status) {
        setQrCode(result.data.qrCode);
        setSecret(result.data.secret);
        setStep(2);
      } else {
        setError(result.message || 'Failed to setup 2FA');
      }
    } catch (err) {
      console.error('2FA setup error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        // Use setTimeout to ensure state is updated
        setTimeout(() => verifyAndEnable(fullCode), 100);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit if complete
      if (pastedData.length === 6) {
        setTimeout(() => verifyAndEnable(pastedData), 100);
      }
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setRecoveryCopied(true);
    setTimeout(() => setRecoveryCopied(false), 2000);
  };

  const finishRecoveryStep = () => {
    if (onComplete) onComplete();
  };

  const verifyAndEnable = async (verificationCode) => {
    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(Utils.baseurl + '2fa/verify', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.token, pending2FAToken),
        body: JSON.stringify({
          userId,
          token: codeToVerify
        }),
      });

      const result = await response.json();

      if (result.status) {
        if (Array.isArray(result.recoveryCodes) && result.recoveryCodes.length > 0) {
          setRecoveryCodes(result.recoveryCodes);
          setStep(4);
        } else if (onComplete) {
          onComplete();
        }
      } else {
        setError(result.message || 'Invalid code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('2FA verify error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // For mandatory setup, use full page layout
  if (isMandatory) {
    return (
      <div className="twofa-setup-page">
        <div className="twofa-setup-content-page">
          {step === 1 && (
            <button className="twofa-corner-back" onClick={onCancel} title="Back to Sign In">
              <span className="back-arrow">←</span>
              <span>Back</span>
            </button>
          )}
          {step === 1 && (
            <div className="twofa-step">
              <div className="twofa-setup-icon-wrapper">
                <div className="twofa-setup-icon">
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
                <div className="twofa-icon-pulse"></div>
              </div>
              <h2>Set Up Two-Factor Authentication</h2>
              <p className="mandatory-notice">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                Two-factor authentication is required to access your account
              </p>
              <p>Add an extra layer of security by requiring a verification code from your authenticator app when signing in.</p>

              <div className="twofa-benefits">
                <div className="benefit-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Protect against unauthorized access</span>
                </div>
                <div className="benefit-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Works with Google Authenticator, Authy, etc.</span>
                </div>
                <div className="benefit-item">
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Quick and easy setup</span>
                </div>
              </div>

              {error && (
                <div className="twofa-error">
                  <span className="error-icon"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                  <span>{error}</span>
                </div>
              )}

              <button
                className="twofa-primary-btn"
                onClick={startSetup}
                disabled={isLoading}
              >
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Get Started'}
              </button>

              {onSkip && (
                <button
                  className="twofa-skip-link"
                  onClick={onSkip}
                  disabled={isLoading}
                >
                  Skip for now
                </button>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="twofa-step">
              <h2>Scan QR Code</h2>
              <p>Open your authenticator app and scan this QR code to add your account.</p>

              <div className="qr-container">
                {qrCode && <img src={qrCode} alt="2FA QR Code" />}
              </div>

              <div className="manual-entry">
                <p>Can't scan? Enter this code manually:</p>
                <div className="secret-key">
                  <code>{secret}</code>
                  <button onClick={copySecret} title="Copy to clipboard">
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  </button>
                </div>
              </div>

              <button className="twofa-primary-btn" onClick={() => setStep(3)}>
                Continue
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="twofa-step">
              <h2>Verify Setup</h2>
              <p>Enter the 6-digit code from your authenticator app to complete setup.</p>

              <div className="code-inputs">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`code-input ${error ? 'error' : ''} ${digit ? 'filled' : ''}`}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>

              {error && <div className="twofa-error">{error}</div>}

              <div className="twofa-buttons">
                <button className="twofa-secondary-btn" onClick={() => setStep(2)}>
                  Back
                </button>
                <button
                  className="twofa-primary-btn"
                  onClick={verifyAndEnable}
                  disabled={isLoading || code.join('').length !== 6}
                >
                  {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Enable 2FA'}
                </button>
              </div>
            </div>
          )}

          {step === 4 && recoveryCodes.length > 0 && (
            <div className="twofa-step">
              <h2>Save your recovery codes</h2>
              <p className="mandatory-notice">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                Each code works once if you lose your phone or uninstall the authenticator app. Store them safely — they will not be shown again.
              </p>
              <div className="twofa-recovery-box">
                <pre className="twofa-recovery-pre">{recoveryCodes.join("\n")}</pre>
                <button type="button" className="twofa-secondary-btn" onClick={copyRecoveryCodes}>
                  <FontAwesomeIcon icon={recoveryCopied ? faCheck : faCopy} /> {recoveryCopied ? "Copied" : "Copy all"}
                </button>
              </div>
              <button className="twofa-primary-btn" onClick={finishRecoveryStep}>
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Modal layout for optional setup (from settings)
  return (
    <div className="twofa-setup-modal">
      <div className="twofa-setup-content">
        <button className="twofa-close-btn" onClick={onCancel}>&times;</button>

        {step === 1 && (
          <div className="twofa-step">
            <div className="twofa-setup-icon-wrapper">
              <div className="twofa-setup-icon">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="twofa-icon-pulse"></div>
            </div>
            <h2>Enable Two-Factor Authentication</h2>
            <p>Add an extra layer of security to your account by requiring a verification code from your authenticator app when signing in.</p>

            <div className="twofa-benefits">
              <div className="benefit-item">
                <FontAwesomeIcon icon={faCheck} />
                <span>Protect against unauthorized access</span>
              </div>
              <div className="benefit-item">
                <FontAwesomeIcon icon={faCheck} />
                <span>Works with Google Authenticator, Authy, etc.</span>
              </div>
              <div className="benefit-item">
                <FontAwesomeIcon icon={faCheck} />
                <span>Quick and easy setup</span>
              </div>
            </div>

            {error && (
              <div className="twofa-error">
                <span className="error-icon"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                <span>{error}</span>
              </div>
            )}

            <button
              className="twofa-primary-btn"
              onClick={startSetup}
              disabled={isLoading}
            >
              {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Get Started'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="twofa-step">
            <h2>Scan QR Code</h2>
            <p>Open your authenticator app and scan this QR code to add your account.</p>

            <div className="qr-container">
              {qrCode && <img src={qrCode} alt="2FA QR Code" />}
            </div>

            <div className="manual-entry">
              <p>Can't scan? Enter this code manually:</p>
              <div className="secret-key">
                <code>{secret}</code>
                <button onClick={copySecret} title="Copy to clipboard">
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                </button>
              </div>
            </div>

            <button className="twofa-primary-btn" onClick={() => setStep(3)}>
              Continue
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="twofa-step">
            <h2>Verify Setup</h2>
            <p>Enter the 6-digit code from your authenticator app to complete setup.</p>

            <div className="code-inputs">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`code-input ${error ? 'error' : ''} ${digit ? 'filled' : ''}`}
                  disabled={isLoading}
                  autoComplete="one-time-code"
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>

            {error && <div className="twofa-error">{error}</div>}

            <div className="twofa-buttons">
              <button className="twofa-secondary-btn" onClick={() => setStep(2)}>
                Back
              </button>
              <button
                className="twofa-primary-btn"
                onClick={verifyAndEnable}
                disabled={isLoading || code.join('').length !== 6}
              >
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Enable 2FA'}
              </button>
            </div>
          </div>
        )}

        {step === 4 && recoveryCodes.length > 0 && (
          <div className="twofa-step">
            <h2>Save your recovery codes</h2>
            <p>Each code works once if you lose access to your authenticator. Store them in a safe place — they will not be shown again.</p>
            <div className="twofa-recovery-box">
              <pre className="twofa-recovery-pre">{recoveryCodes.join("\n")}</pre>
              <button type="button" className="twofa-secondary-btn" onClick={copyRecoveryCodes}>
                <FontAwesomeIcon icon={recoveryCopied ? faCheck : faCopy} /> {recoveryCopied ? "Copied" : "Copy all"}
              </button>
            </div>
            <button className="twofa-primary-btn" onClick={finishRecoveryStep}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TwoFactorSetup;
